/**
 * Area Operations - Funktioner för att hantera area selection
 * 
 * Använder districtCache för att hämta/uppdatera selected areas
 */

import * as mapState from '../map/map-state';
import * as districtCache from '../districts/district-cache';
import * as turf from '../utils/turf';

/**
 * Välj en area (lägg till i selection)
 * @param {Object} area - GeoJSON feature
 * @param {Function} onChanged - Optional callback som anropas med nya selectedAreas
 */
export function selectArea(area, onChanged = null) {
    const currentDistrictId = districtCache.getCurrentDistrictId();
    if (!currentDistrictId) {
        console.warn('selectArea: No current district ID');
        return;
    }
    
    const district = districtCache.get(currentDistrictId);
    if (!district) {
        console.warn('selectArea: District not found in cache');
        return;
    }
    
    const selectedAreas = [...(district.selectedAreas || [])];
    if (!selectedAreas.find(a => a.properties.id === area.properties.id)) {
        const newSelectedAreas = [...selectedAreas, {
            ...area
        }];
        
        // Spara i cache med isDirty flag
        districtCache.saveCurrent({
            ...district,
            selectedAreas: newSelectedAreas,
            isDirty: true
        });
        
        // Anropa callback direkt om den finns (med nya selectedAreas)
        if (onChanged) {
            onChanged(newSelectedAreas);
        }
    } else if (onChanged) {
        // Om area redan är vald, anropa callback ändå med nuvarande selectedAreas
        onChanged(selectedAreas);
    }
}

/**
 * Toggle en area (lägg till om den inte finns, ta bort om den finns)
 * @param {Object} area - GeoJSON feature
 * @param {Function} onChanged - Optional callback som anropas med nya selectedAreas
 * @returns {Array} Nya selected areas array
 */
export function toggleArea(area, onChanged = null) {
    const currentDistrictId = districtCache.getCurrentDistrictId();
    if (!currentDistrictId) {
        console.warn('toggleArea: No current district ID');
        return [];
    }
    
    const district = districtCache.get(currentDistrictId);
    if (!district) {
        console.warn('toggleArea: District not found in cache');
        return [];
    }
    
    const selectedAreas = district.selectedAreas || [];
    const areaId = area.properties.id;
    const existingIndex = selectedAreas.findIndex(a => a && a.properties && a.properties.id === areaId);

    let newSelectedAreas;
    if (existingIndex === -1) {
        // Area not selected, add it
        newSelectedAreas = [...selectedAreas, area];
    } else {
        // Area already selected, remove it
        newSelectedAreas = selectedAreas.filter((_, index) => index !== existingIndex);
    }

    // Filtrera bort ogiltiga areas
    newSelectedAreas = newSelectedAreas.filter(area => area && area.properties && area.properties.id);
    
    // Spara i cache med isDirty flag
    districtCache.saveCurrent({
        ...district,
        selectedAreas: newSelectedAreas,
        isDirty: true
    });
    
    // Om vi har selected areas men inget selection type, sätt det baserat på första area's type
    const currentSelectionType = district.selectionType || 'clear';
    if (newSelectedAreas.length > 0 && currentSelectionType === 'clear') {
        const firstArea = newSelectedAreas[0];
        if (firstArea && firstArea.properties && firstArea.properties.type) {
            const selectionType = firstArea.properties.type;
            mapState.setSelectionType(selectionType);
            
            // Spara selectionType i cache med isDirty flag
            districtCache.saveCurrent({
                ...district,
                selectionType,
                isDirty: true
            });
        }
    }
    
    // Anropa callback direkt om den finns
    if (onChanged) {
        onChanged(newSelectedAreas);
    }
    
    return newSelectedAreas;
}

/**
 * Konvertera selected areas till current selection areas
 * Hanterar överlappande zip-areas och tar bort mindre areas som är inuti större
 * @param {Object} areaOuter - Yttre area (GeoJSON feature)
 */
export function convertSelectedToCurrentSelectionAreas(areaOuter) {
    const currentDistrictId = districtCache.getCurrentDistrictId();
    if (!currentDistrictId) {
        console.warn('convertSelectedToCurrentSelectionAreas: No current district ID');
        return;
    }
    
    const district = districtCache.get(currentDistrictId);
    if (!district) {
        console.warn('convertSelectedToCurrentSelectionAreas: District not found in cache');
        return;
    }
    
    let removeAreas = [];
    const selectedAreas = [...(district.selectedAreas || [])];
        
    selectedAreas.forEach(areaInner => {
        const polyOuter = areaOuter.geometry.type === 'GeometryCollection' ? 
            turf.multiPolygon(areaOuter.geometry.geometries.map(g => g.coordinates)) : 
            turf.polygon(areaOuter.geometry.coordinates);
        const polyInner = areaInner.geometry.type === 'GeometryCollection' ? 
            turf.multiPolygon(areaInner.geometry.geometries.map(g => g.coordinates)) : 
            turf.polygon(areaInner.geometry.coordinates);
        
        if (areaOuter.properties.type.indexOf('zip') > -1 && 
            areaInner.properties.type.indexOf('zip') > -1 && 
            areaOuter.properties.type !== areaInner.properties.type && 
            turf.intersect(polyOuter, polyInner)) {
            if (turf.area(polyOuter) > turf.area(polyInner)) {
                removeAreas = [...removeAreas, areaInner.id];
            } else {
                removeAreas = [...removeAreas, areaOuter.id];
            }
        } 
    });

    const filteredAreas = selectedAreas.filter(a => !removeAreas.includes(a.id));
    
    // Spara i cache med isDirty flag
    districtCache.saveCurrent({
        ...district,
        selectedAreas: filteredAreas,
        isDirty: true
    });
}
